import { createContext, useContext, useState, ReactNode } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import './ConfirmationPopup.css';
import Button from './Buttons';

interface ConfirmationState {
  isOpen: boolean;
  message: string;
  onConfirm: (() => void) | null;
}

interface ConfirmationContextType {
  showConfirmation: (message: string, onConfirm: () => void) => void;
}

const ConfirmationContext = createContext<ConfirmationContextType | null>(null);

export const useConfirmation = (): ConfirmationContextType => {
  const context = useContext(ConfirmationContext);
  if (!context) {
    throw new Error('useConfirmation must be used within a ConfirmationProvider');
  }
  return context;
};

interface ConfirmationProviderProps {
  children: ReactNode;
}

export const ConfirmationProvider = ({ children }: ConfirmationProviderProps) => {
  const [confirmation, setConfirmation] = useState<ConfirmationState>({
    isOpen: false,
    message: '',
    onConfirm: null,
  });

  const showConfirmation = (message: string, onConfirm: () => void): void => {
    setConfirmation({ isOpen: true, message, onConfirm });
  };

  const handleConfirm = (): void => {
    if (confirmation.onConfirm) {
      confirmation.onConfirm();
    }
    closeConfirmation();
  };

  const closeConfirmation = (): void => {
    setConfirmation({ isOpen: false, message: '', onConfirm: null });
  };

  return (
    <ConfirmationContext.Provider value={{ showConfirmation }}>
      {children}
      <AnimatePresence>
        {confirmation.isOpen && (
          <motion.div
            className="confirmation-popup-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="confirmation-popup"
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.8 }}
              transition={{ duration: 0.3 }}
            >
              <strong>Sure about this action?</strong>
              <span>{confirmation.message}</span>
              <div className="confirmation-popup-buttons">
                <Button variant="primary" size="large" onClick={handleConfirm}>
                  Confirm
                </Button>
                <Button variant="danger" size="large" onClick={closeConfirmation}>
                  Cancel
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </ConfirmationContext.Provider>
  );
};
